/* eslint-disable import/prefer-default-export */
import { takeEvery, put, putResolve, select, delay, getContext, call, race } from 'redux-saga/effects';
import { getLogger } from '../../logger/index';
import { waitForList } from '../../redux/entity-descriptor';
import { sendWelcomeMessage } from '../chat/actions';
import { getLocale, getMessages } from '../intl/selectors';
import {
  sendUsersList,
  REQUEST_CONTACT,
  requestContactFailed,
  requestContactSuccess,
  newCustomer,
  NEW_CUSTOMER,
} from './actions';
import { newCustomerCall } from '../communicationVideo/actions';
import { contact } from './entities';
import { encodeActionType, MessageType, notify } from '../notification/index';

const logger = getLogger('communication');

function* newCustomerSaga({ payload: customer }) {
  // customer = { customerChat, customerVideoCall, username, name, isAnonymousUser }
  try {
    const messages = yield select(getMessages);
    let contacts = yield select(contact.selectors.getList);

    if (!Array.isArray(contacts) || contacts.length === 0) {
      contacts = yield waitForList(contact);
    }
    const isContactLoaded = contacts.some(({ id }) => id === customer.username);

    if (!isContactLoaded) {
      logger.debug('add new customer as a contact');
      yield putResolve(contact.addListItem(transformNewCustomerToContact(customer)));

      logger.debug('Send users list to WS server');
      const contacts = yield select(contact.selectors.getList);
      yield put(sendUsersList(contacts.map(({ id }) => id)));
    } else {
      // update existing entry to be online again
      yield putResolve(contact.removeListItem(customer.username));
      yield putResolve(contact.addListItem(transformNewCustomerToContact(customer)));
    }
    yield put(
      notify(
        MessageType.information,
        `${messages.endCustomerNewMessage}: ${customer.name}`,
        {
          actions: [{ title: messages.start_chat, action: encodeActionType('VIEW_CUSTOMER_CHAT', customer) }],
          requireInteraction: true,
          timestamp: Date.now(),
          dismissAfter: -1,
        },
        true
      )
    );

    yield delay(1000);
    logger.debug('Trigger sending welcome message');
    yield put(sendWelcomeMessage(customer));
  } catch (e) {
    logger.error('newCustomerSaga', e);
  }
}

function transformNewCustomerToContact(customerData) {
  return {
    id: customerData.username,
    is_anonymous_user: customerData.isAnonymousUser,
    first_name: customerData.name,
    last_name: '',
    customer_chat_id: customerData.customerChat,
    customer_video_call_id: customerData.customerVideoCall,
    is_audio_call_available: true,
    is_mobile_online: false,
    is_online: true,
    is_video_call_available: true,
    last_seen: new Date(),
    name: customerData.name,
    role: customerData.role,
    unseen_messages: 0,
  };
}

function* requestContactSaga({ payload }) {
  const http = yield getContext('http');
  const locale = yield select(getLocale);

  const requestUrl = `/api/contact.json?&_locale=${locale}`;
  const requestConfig = {};
  try {
    const { response } = yield race({
      response: call(http.post, requestUrl, payload, requestConfig),
      timeout: delay(15000),
    });
    if (!response || response.status < 200 || response.status > 299) {
      let error;
      if (response) {
        logger.error('Response with HTTP status', response.status, response);
        error = response.data;
      } else {
        error = new Error('Request timed out.');
      }
      yield put(requestContactFailed(error));
    } else {
      const { data } = response;
      logger.debug('Successfully requested contact', data);
      yield put(requestContactSuccess(data));
    }
  } catch (e) {
    logger.error(e);
    yield put(requestContactFailed(e));
  }
}

export default function* watchNewCustomerSaga() {
  // for employee
  yield takeEvery(NEW_CUSTOMER, newCustomerSaga);
  // for customer
  yield takeEvery(REQUEST_CONTACT, requestContactSaga);

  const isMobile = yield getContext('isMobile');

  if (isMobile) {
    return;
  }

  logger.debug('Watch new customer action');
  yield takeEvery(
    action => action.type === 'SOCKET_EVENT' && action.payload.eventName === 'new_customer',
    function*({ payload }) {
      logger.debug('Trigger newCustomer by socket event');
      yield put(
        ['audio', 'video'].includes(payload.args.type) ? newCustomerCall(payload.args) : newCustomer(payload.args)
      );
    }
  );
}
